import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { FaCubes } from 'react-icons/fa';

const MODULE_PALETTE = {
  iap_admin: { label: 'IAP Admin', color: '#6366f1' },
  content_studio: { label: 'Content Studio', color: '#0ea5e9' },
  player: { label: 'Runtime Player', color: '#10b981' },
  extension_player: { label: 'Extension Player', color: '#f59e0b' },
  default: { label: 'Other Modules', color: '#8b5cf6' }
};

export const formatModuleName = (key) => {
  if (MODULE_PALETTE[key]) return MODULE_PALETTE[key].label;
  if (!key) return 'Unknown Module';
  return key
    .split(/[_,-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="analytics-chart-tooltip" data-testid="module-doughnut-tooltip">
        <p className="tooltip-title">{d.name}</p>
        <p className="tooltip-id">Key: <code>{d.moduleKey}</code></p>
        <p className="tooltip-value" style={{ color: d.color }}>
          <strong>{d.quantity.toLocaleString()}</strong> events ({d.percent}%)
        </p>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array
};

export default function ModuleBreakdownDoughnut({ moduleUsage = [], height = 320 }) {
  const { chartData, totalQuantity } = useMemo(() => {
    if (!Array.isArray(moduleUsage) || moduleUsage.length === 0) {
      return { chartData: [], totalQuantity: 0 };
    }

    const items = moduleUsage.map((m) => {
      const key = m.moduleKey || 'unknown';
      const meta = MODULE_PALETTE[key] || MODULE_PALETTE.default;
      // Extract activity events only (exclude storage bytes to ensure apples-to-apples event comparison)
      const countDims = Array.isArray(m.dimensions)
        ? m.dimensions.filter((dim) => String(dim.unit).toLowerCase() === 'count' || String(dim.dimension).toLowerCase() !== 'storage')
        : [];
      const qty = countDims.length > 0
        ? countDims.reduce((acc, dim) => acc + Number(dim.quantity || 0), 0)
        : (String(m.unit).toLowerCase() === 'count' || !m.dimensions ? Number(m.quantity || 0) : 0);

      return {
        moduleKey: key,
        name: formatModuleName(key),
        quantity: qty,
        color: meta.color
      };
    }).filter((item) => item.quantity > 0);

    const total = items.reduce((sum, item) => sum + item.quantity, 0);

    const withPercent = items.map((item) => ({
      ...item,
      percent: total > 0 ? ((item.quantity / total) * 100).toFixed(1) : '0'
    }));

    return { chartData: withPercent, totalQuantity: total };
  }, [moduleUsage]);

  if (chartData.length === 0) {
    return (
      <div className="chart-empty-state" data-testid="module-doughnut-empty">
        <FaCubes className="empty-icon" />
        <p>No module telemetry recorded for this measurement window.</p>
      </div>
    );
  }

  return (
    <div className="module-doughnut-card" data-testid="module-breakdown-doughnut">
      <div className="chart-card-header">
        <div>
          <h3 className="chart-card-title">Module Activity Distribution</h3>
          <p className="chart-card-subtitle">Activity event distribution across Cognior IAP client modules</p>
        </div>
        <div className="module-total-badge">
          Total: <strong>{totalQuantity.toLocaleString()}</strong> events
        </div>
      </div>

      <div className="chart-wrapper" style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry) => (
                <span style={{ color: '#475569', fontSize: '13px', fontWeight: 500 }}>
                  {value} ({entry.payload.percent}%)
                </span>
              )}
            />
            <Pie
              data={chartData}
              dataKey="quantity"
              nameKey="name"
              cx="50%"
              cy="46%"
              innerRadius={65}
              outerRadius={95}
              paddingAngle={4}
            >
              {chartData.map((entry) => (
                <Cell key={`slice-${entry.moduleKey}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

ModuleBreakdownDoughnut.propTypes = {
  moduleUsage: PropTypes.array,
  height: PropTypes.number
};
